import { convert } from "html-to-text";
import { useMemo } from "react";
export const descriptionSplitByPTag = (str) => {
  try {
    if (!str) {
      return [];
    }

    let outputArr = str.split("</p>");
    outputArr = outputArr.map((data) => data + "</p>");

    let finalArray = [];

    // helper to merge incomplete tag blocks (blockquote, table, etc.)
    const mergeUntilBalanced = (arr, startIndex, tagName) => {
      let openingCount =
        arr[startIndex].split(`<${tagName}`).length - 1;
      let closingCount =
        arr[startIndex].split(`</${tagName}>`).length - 1;

      if (openingCount !== closingCount) {
        let ind = startIndex;
        while (true) {
          startIndex++;
          arr[ind] = arr[ind] + arr[startIndex];

          const openingInternal =
            arr[startIndex]?.split(`<${tagName}`).length - 1;
          const closingInternal =
            arr[startIndex]?.split(`</${tagName}>`).length - 1;

          arr[startIndex] = "";

          openingCount += openingInternal;
          closingCount += closingInternal;

          if (openingCount === closingCount) {
            break;
          }
        }
      }
    };

    for (let x = 0; x < outputArr.length; x++) {
      if (!outputArr[x]) continue;

      // balance blockquotes
      mergeUntilBalanced(outputArr, x, "blockquote");

      // balance tables
      mergeUntilBalanced(outputArr, x, "table");
    }

    // cleanup empty entries
    for (let x = 0; x < outputArr.length; x++) {
      if (outputArr[x] && outputArr[x].length !== 0) {
        finalArray.push(outputArr[x]);
      }
    }

    return finalArray;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export function getFirstViewportHtmlSSR(html) {
  try{

  if (!html) return "";

  // Remove heavy blocks (cheap, single regex pass)
  const withoutHeavy = html.replace(
    /<(blockquote|table|iframe|embed|object|video|audio|figure)[\s\S]*?<\/\1>/gi,
    ""
  );

  // Find first paragraph only
  const start = withoutHeavy.search(/<p\b/i);
  if (start === -1) return "";

  const end = withoutHeavy.indexOf("</p>", start);
  if (end === -1) return "";

  // Slice just what we need
  const firstP = withoutHeavy.slice(start, end + 4);

  // Strip tags → text
  const text = firstP
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return `<p>${text}</p>`;
  }catch(err){
    console.log("Error while rendering viewport html: ", err);
}

}



const VOID_TAGS = new Set([
  "area", "base", "br", "col", "embed", "hr", "img", "input",
  "link", "meta", "param", "source", "track", "wbr",
]);

const HTML_TOKEN =
  /<!--[\s\S]*?-->|<(style|textarea)\b[\s\S]*?<\/\1\s*>|<(\/?)([a-zA-Z][\w:-]*)(?:[^>"']|"[^"]*"|'[^']*')*?(\/?)>/gi;


export function balanceHtmlChunk(chunk = "") {
  try {
    const open = []; // [tagName, index of its entry in `out`]
    const out = [];
    let last = 0;
    let match;
    HTML_TOKEN.lastIndex = 0;
    while ((match = HTML_TOKEN.exec(chunk)) !== null) {
      out.push(chunk.slice(last, match.index));
      last = HTML_TOKEN.lastIndex;
      const [token, , isEnd, rawName, selfClosing] = match;
      const name = rawName?.toLowerCase();
      if (!name || VOID_TAGS.has(name) || selfClosing) {
        out.push(token); // comment, raw-text block, void or self-closing tag
      } else if (!isEnd) {
        open.push(name);
        out.push(token);
      } else {
        const at = open.lastIndexOf(name);
        if (at === -1) continue; // stray end tag: drop it
        open.length = at; // closes it and anything left open inside it
        out.push(token);
      }
    }
    out.push(chunk.slice(last));
    for (let i = open.length - 1; i >= 0; i--) out.push(`</${open[i]}>`);
    return out.join("");
  } catch (err) {
    console.error("Error while balancing article html", err);
    return chunk;
  }
}

export function prepareArticleBodyHtml(html = "") {
  if (!html) return "";
  return html
    .replace(/<script\b[\s\S]*?<\/script\s*>/gi, "")
    .replace(/<script\b[^>]*\/?>/gi, "")
    .replace(/<(img|iframe)\b(?![^>]*\bloading\s*=)/gi, '<$1 loading="lazy"')
    // embeds (YouTube etc.) load on first scroll, see activateDeferredIframes
    .replace(/<iframe\b([^>]*?)\ssrc\s*=/gi, "<iframe$1 data-src=");
}

export function activateDeferredIframes(root = document) {
  root.querySelectorAll("iframe[data-src]").forEach((frame) => {
    frame.src = frame.dataset.src;
    frame.removeAttribute("data-src");
  });
}


export function useProgressiveParagraphs(html) {
  return useMemo(
    () =>
      html
        ? descriptionSplitByPTag(prepareArticleBodyHtml(html)).map(balanceHtmlChunk)
        : [],
    [html]
  );
}

export function splitHtmlByParagraph(htmlString) {
  // Regular expression to match content between <p> and </p> tags
  const regex = /<p>(.*?)<\/p>/g;

  // Array to hold the extracted paragraphs
  let paragraphs = [];

  // Extract paragraphs
  let match;
  while ((match = regex.exec(htmlString)) !== null) {
    paragraphs.push(match[1]);
  }

  return paragraphs;
}
export function splitHTMLArticle(articleHTML) {
  function findSubstringIndices(inputString, substring) {
    const indices = [];
    let index = inputString.indexOf(substring);

    while (index !== -1) {
      indices.push(index);
      index = inputString.indexOf(substring, index + 1);
    }

    return indices;
  }

  const inputString = articleHTML?.replace(/\s+xml:lang="[^"]*"/gi, "")
    .replace(/\s+nirmala/gi, " ")
    .replace(/\s+ui/gi, " ")
    .replace(/\s+"="/gi, "", ""); //articleHtmlgoesHere
  const substring = "<p>";
  const substring1 = "</p>";

  const pOpening = findSubstringIndices(inputString, substring);
  const pClosing = findSubstringIndices(inputString, substring1);

  var finalArray = [];
  let sIndex = 0;
  if (pOpening[0] !== 0) {
    finalArray.push(inputString.substring(0, pOpening[0]));
  }
  finalArray.push(inputString.substring(pOpening[0], pClosing[0] + 4));
  sIndex++;
  while (sIndex < pOpening.length) {
    if (pClosing[sIndex - 1] + 4 !== pOpening[sIndex]) {
      const embedInBtw = inputString.substring(
        pClosing[sIndex - 1] + 4,
        pOpening[sIndex]
      );
      finalArray.push(embedInBtw);
    }
    const pTag = inputString.substring(pOpening[sIndex], pClosing[sIndex] + 4);
    finalArray.push(pTag);
    sIndex++;
  }
  // remove meta tags from the article body
  return finalArray.map((element) => element.replace(/<meta[^>]*>/g, ""));
}

export function splitHTMLArticleMin(articleHTML) {
  // Remove unwanted amp tags + their parent divs
  function stripAmpEmbeds(html) {
    // Regex to remove <div>...</div> that contains amp-iframe, amp-twitter, or amp-youtube
    return html
      .replace(/<div[^>]*>\s*<amp-iframe[\s\S]*?<\/amp-iframe>\s*<\/div>/gi, "")
      .replace(/<div[^>]*>\s*<amp-twitter[\s\S]*?<\/amp-twitter>\s*<\/div>/gi, "")
      .replace(/<div[^>]*>\s*<amp-youtube[\s\S]*?<\/amp-youtube>\s*<\/div>/gi, "")
      // In case amp-* tags are not wrapped in a div
      .replace(/<amp-iframe[\s\S]*?<\/amp-iframe>/gi, "")
      .replace(/<amp-twitter[\s\S]*?<\/amp-twitter>/gi, "")
      .replace(/<amp-youtube[\s\S]*?<\/amp-youtube>/gi, "")
     
  }

  function findSubstringIndices(inputString, substring) {
    const indices = [];
    let index = inputString.indexOf(substring);

    while (index !== -1) {
      indices.push(index);
      index = inputString.indexOf(substring, index + 1);
    }

    return indices;
  }

  const inputString = stripAmpEmbeds(articleHTML);
  const substring = "<p>";
  const substring1 = "</p>";

  const pOpening = findSubstringIndices(inputString, substring);
  const pClosing = findSubstringIndices(inputString, substring1);

  var finalArray = [];
  let sIndex = 0;

  if (pOpening[0] !== 0) {
    finalArray.push(inputString.substring(0, pOpening[0]));
  }

  if (pOpening.length > 0 && pClosing.length > 0) {
    finalArray.push(inputString.substring(pOpening[0], pClosing[0] + 4));
    sIndex++;
  }

  while (sIndex < pOpening.length) {
    if (pClosing[sIndex - 1] + 4 !== pOpening[sIndex]) {
      const embedInBtw = inputString.substring(
        pClosing[sIndex - 1] + 4,
        pOpening[sIndex]
      );
      finalArray.push(embedInBtw);
    }
    const pTag = inputString.substring(pOpening[sIndex], pClosing[sIndex] + 4);
    finalArray.push(pTag);
    sIndex++;
  }

  return finalArray;
}

export function getPlainText(htmlText) {
  try {
    const plainText = convert(htmlText, {
      wordwrap: false, // Disable word wrapping
      ignoreImage: true, // Ignore images
      preserveNewlines: true, // Preserve newlines
      uppercaseHeadings: false, // Do not convert headings to uppercase
    });
    return plainText;
  } catch (err) {
    console.log("err while converting to plain text", err);
    return htmlText;
  }
}

export function capitalizeWords(str) {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
